<template>
  <div class="breast-harmony-form">
    <div class="form-container">
      <h1>Entdecken Sie Ihren idealen Weg zur Brustvergrößerung mit <span class="brand">theaesthetics</span>!</h1>
      <p class="subtitle">Nur wenige Schritte trennen Sie von Ihrer persönlichen Behandlungsempfehlung</p>

      <p class="intro-text">
        Bei <span class="brand"><a href="https://theaesthetics.at" target="_blank">theaesthetics</a></span> steht Ihr persönliches Wohlbefinden im Mittelpunkt. Wir freuen uns,
        dass Sie den Weg zu uns gefunden haben - sei es über Instagram oder unsere Website. Bevor wir mit der kurzen
        Analyse beginnen, möchten wir Ihnen zeigen, wie wir gemeinsam Ihre Wünsche verwirklichen können.
      </p>

      <div class="info-sections">
        <div class="info-section card">
          <h2><span class="brand"><a href="https://theaesthetics.at" target="_blank">theaesthetics</a></span> & die Behandlung</h2>
          <p>Wir verbinden modernste ästhetische Technologien mit individuell abgestimmten Behandlungsmethoden. Unser
            Fokus liegt auf nachhaltigen Ergebnissen, Ihrem Komfort und Ihrem langfristigen Wohlbefinden.</p>
        </div>

        <div class="info-section card">
          <h2>Was Sie erwarten können</h2>
          <p>Minimale Ausfallzeit, maximale Resultate: Unsere Behandlungen sind darauf ausgerichtet, Ihr
            Selbstbewusstsein zu stärken. Sie sind in den Händen unserer spezialisierten Experten bestens aufgehoben.
          </p>
        </div>
      </div>

      <p class="assessment-intro">
        Beantworten Sie die folgenden Fragen ehrlich, damit wir die für Sie optimale Behandlungsmethode ermitteln
        können. Ihre Antworten helfen uns, eine maßgeschneiderte Empfehlung für Sie zu erstellen. Für Ihre persönliche
        Auswertung benötigen wir Ihre Kontaktdaten.
      </p>

      <h2 class="assessment-title">Ihr persönlicher <span class="brand">theaesthetics</span> Eignungstest</h2>

      <form @submit.prevent="submitForm" class="form" v-if="!showResults">
        <div class="personal-data">
          <div class="form-row">
            <div class="form-group">
              <input type="text" v-model="formData.firstName" placeholder="Vorname *" required />
            </div>
            <div class="form-group">
              <input type="text" v-model="formData.lastName" placeholder="Nachname *" required />
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <input
                type="email"
                v-model="formData.email"
                placeholder="E-Mail *"
                required
                :class="{ 'input-error': emailError }"
              />
              <div v-if="emailError" class="error-message">{{ emailError }}</div>
            </div>
            <div class="form-group">
              <input type="tel" v-model="formData.phone" placeholder="Telefon" required />
            </div>
          </div>
        </div>

        <div class="questions">
          <div v-for="(question, index) in questions" :key="index" class="question"
            :style="{ '--delay': `${index * 0.1}s` }">
            <h3>{{ question.text }}</h3>
            <div class="options">
              <label v-for="option in question.options" :key="option.value">
                <input type="radio" :name="'question' + index" :value="option.points" v-model="formData.answers[index]"
                  required />
                {{ option.text }}
              </label>
            </div>
          </div>
        </div>

        <div class="form-group additional-questions">
          <label for="weitere_fragen">Haben Sie noch Fragen an das Team von theaesthetics?</label>
          <textarea id="weitere_Fragen" v-model="formData.weitere_fragen" placeholder="Ihre Fragen..."
            rows="4"></textarea>
        </div>

        <div class="consent">
          <div class="consent-item">
            <div class="checkbox-wrapper">
              <input type="checkbox" v-model="formData.consent" required id="consent-checkbox" />
              <label for="consent-checkbox" class="checkbox-label"></label>
            </div>
            <div class="consent-text">
              <label for="consent-checkbox">
                Ich stimme der Verarbeitung meiner Daten gemäß 
                <a href="#" @click.prevent="showPrivacyModal = true">der Datenschutzerklärung</a> zu
              </label>
            </div>
          </div>
          <div class="consent-item">
            <div class="checkbox-wrapper">
              <input type="checkbox" v-model="formData.newsletter" id="newsletter-checkbox" />
              <label for="newsletter-checkbox" class="checkbox-label"></label>
            </div>
            <div class="consent-text">
              <label for="newsletter-checkbox">
                Ja, ich möchte regelmäßig Neuigkeiten und Informationen sowie Angebote von theaesthetics erhalten.
              </label>
            </div>
          </div>
        </div>

        <button type="submit" class="submit-button" :disabled="isSubmitting || !formData.consent || !!emailError">
          <span v-if="!isSubmitting">Analyse starten & absenden</span>
          <div v-else class="loader"></div>
        </button>
      </form>

      <div v-if="showResults" class="result-container">
        <div class="result-header">
          <h2>Ihre persönliche Auswertung</h2>
          <p class="subtitle">Basierend auf Ihren individuellen Antworten</p>
        </div>

        <div class="result-grid">
          <div class="score-card">
            <div class="progress-ring-container">
              <svg class="progress-ring" :width="200" :height="200">
                <circle class="progress-ring-circle-bg" :stroke-width="12" fill="transparent" r="85" cx="100"
                  cy="100" />
                <circle class="progress-ring-circle" :stroke-width="12" fill="transparent" r="85" cx="100" cy="100"
                  :style="{
                    strokeDasharray: `${2 * Math.PI * 85}`,
                    strokeDashoffset: `${2 * Math.PI * 85 * (1 - analysisResult.score / 100)}`
                  }" />
                <text x="100" y="90" class="percentage">{{ analysisResult.score }}%</text>
                <text x="100" y="120" class="percentage-label">Eignung</text>
              </svg>
            </div>
            <div class="score-details">
              <div class="points">
                <span class="current">{{ analysisResult.totalPoints }}</span>
                <span class="separator">/</span>
                <span class="max">{{ analysisResult.maxPoints }}</span>
                <span class="label">Punkte</span>
              </div>
            </div>
          </div>

          <div class="recommendation-card">
            <div class="recommendation-header">
              <i class="recommendation-icon">💡</i>
              <h3>Unsere Empfehlung</h3>
            </div>
            <p class="recommendation-text">{{ analysisResult.resultText }}</p>
            <div class="recommendation-tags">
              <span class="tag" :class="getTagClass(analysisResult.score)">
                {{ getEignungText(analysisResult.score) }}
              </span>
            </div>
          </div>
        </div>
        <div class="next-steps-section">
          <h3>Ihre nächsten Schritte</h3>
          <div class="steps-grid">
            <div class="step-card">
              <div class="step-number">1</div>
              <h4>Persönliche Beratung</h4>
              <p>Vereinbaren Sie ein kostenloses Beratungsgespräch mit unseren Experten.</p>
              <button class="primary-button" @click="bookConsultation">
                Termin vereinbaren
              </button>
            </div>
            <div class="step-card">
              <div class="step-number">2</div>
              <h4>Informationen</h4>
              <p>Erhalten Sie detaillierte Informationen zu Ihrer individuellen Behandlung.</p>
              <button class="secondary-button" @click="downloadInfo">
                weitere Informationen
              </button>
            </div>
          </div>
        </div>
        <div class="email-text">
          <p>Sie haben eine E-Mail mit detaillierten Informationen erhalten.</p>
        </div>
        <div class="action-buttons">
          <button class="reset-button" @click="resetForm">
            <i class="reset-icon">↺</i>
            Neue Analyse starten
          </button>
        </div>
      </div>
    </div>
    <DatenschutzerklaerungModal v-if="showPrivacyModal" @close="showPrivacyModal = false" />
  </div>
</template>

<script>
import { ref, reactive, watch } from 'vue'
import axios from 'axios'
import DatenschutzerklaerungModal from './DatenschutzerklaerungModal.vue'

export default {
  name: 'BreastHarmonyForm',
  components: { DatenschutzerklaerungModal },
  setup() {
    const questions = [
      {
        text: "Wie groß ist Ihre aktuelle Brustgröße?",
        options: [
          { text: "Klein (A-B Körbchen)", points: 10 },
          { text: "Mittel (C Körbchen)", points: 8 },
          { text: "Groß (D+ Körbchen)", points: 6 }
        ]
      },
      {
        text: "Haben Sie das Gefühl, dass Ihre Brüste erschlafft sind?",
        options: [
          { text: "Keine Erschlaffung", points: 10 },
          { text: "Minimale Erschlaffung", points: 9 },
          { text: "Mäßige Erschlaffung", points: 8 },
          { text: "Starke Erschlaffung", points: 6 }
        ]
      },
      {
        text: "Halten Sie Ihre Brüste für asymmetrisch?",
        options: [
          { text: "Keine Asymmetrie", points: 10 },
          { text: "Minimale Asymmetrie", points: 9 },
          { text: "Mäßige Asymmetrie", points: 8 },
          { text: "Starke Asymmetrie", points: 6 }
        ]
      },
      {
        text: "Hatten Sie bereits eine Brustoperation?",
        options: [
          { text: "Nein, keine frühere Operation", points: 10 },
          { text: "Ja, frühere Operation", points: 0 }
        ]
      },
      {
        text: "Haben Sie das Gefühl, dass Sie eine Bruststraffung benötigen?",
        options: [
          { text: "Keine Straffung erforderlich", points: 10 },
          { text: "Vielleicht, aber unsicher", points: 8 },
          { text: "Ja, ich denke, ich brauche eine Straffung", points: 4 }
        ]
      },
      {
        text: "Möchten Sie eine deutliche Vergrößerung?",
        options: [
          { text: "Nein, nur eine dezente/natürliche Verbesserung", points: 10 },
          { text: "Ja, ich möchte eine deutliche Vergrößerung", points: 4 }
        ]
      },
      {
        text: "Legen Sie Wert auf ein natürliches Ergebnis?",
        options: [
          { text: "Ja, ich bevorzuge ein natürliches Ergebnis", points: 10 },
          { text: "Nein, ich möchte eine auffälligere Veränderung", points: 4 }
        ]
      },
      {
        text: "Haben Sie eine begrenzte Zeit für die Erholungsphase?",
        options: [
          { text: "Ja, ich möchte eine kurze Erholungszeit", points: 10 },
          { text: "Nein, eine längere Erholung ist für mich in Ordnung", points: 6 }
        ]
      },
      {
        text: "Haben Sie Angst vor einer Vollnarkose?",
        options: [
          { text: "Ja, ich möchte eine Vollnarkose vermeiden", points: 10 },
          { text: "Nein, eine Vollnarkose ist für mich in Ordnung", points: 8 }
        ]
      },
      {
        text: "Sind Sie besorgt über Narbenbildung?",
        options: [
          { text: "Ja, ich möchte Narben vermeiden", points: 10 },
          { text: "Nein, Narben stören mich nicht", points: 6 }
        ]
      }
    ]

    const formData = reactive({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      answers: [],
      consent: false,
      newsletter: true,
      weitere_fragen: ''
    })

    const emailError = ref('')

    const validateEmail = (email) => {
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return re.test(email)
    }

    watch(() => formData.email, (newVal) => {
      if (!newVal) {
        emailError.value = 'Bitte geben Sie Ihre E-Mail-Adresse ein.'
      } else if (!validateEmail(newVal)) {
        emailError.value = 'Bitte geben Sie eine gültige E-Mail-Adresse ein.'
      } else {
        emailError.value = ''
      }
    })

    const isSubmitting = ref(false)
    const showResults = ref(false)
    const analysisResult = ref(null)
    const showPrivacyModal = ref(false)

    const calculateScore = () => {
      const totalPoints = formData.answers.reduce((sum, points) => sum + (points || 0), 0)
      const maxPoints = questions.length * 10
      const score = Math.round((totalPoints / maxPoints) * 100)
      return {
        score,
        totalPoints,
        maxPoints,
        resultText: getRecommendation(score)
      }
    }

    const getRecommendation = (score) => {
      if (score >= 70) {
        return "Sie sind eine ideale Kandidatin für MIA Femtech - Brustharmonisierung. Basierend auf Ihren Antworten entspricht dieses Verfahren genau Ihren aktuellen Zielen. Ihre Erwartungen decken sich mit den Möglichkeiten dieser Behandlung."
      } else {
        return "Ihrer Antworten zeigen, dass andere bewährte Techniken für Sie eine Lösung sind. Basierend auf Ihren Antworten könnte eine klassische Brustvergrößerung oder eine moderate Straffung die passende Lösung für Sie sein."
      }
    }

    const submitForm = async () => {
      try {
        isSubmitting.value = true
        const result = calculateScore()

        // Sende Daten an den Server
        await axios.post(`${process.env.VUE_APP_API_URL}/analyze-breast`, {
          ...formData,
          score: result.score,
          recommendation: result.resultText
        }, {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        })

        analysisResult.value = {
          score: result.score,
          totalPoints: result.totalPoints,
          maxPoints: result.maxPoints,
          resultText: result.resultText
        }
        showResults.value = true
      } catch (error) {
        console.error('Fehler beim Senden der Daten:', error)
        alert('Es ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut. ' +
          (error.response?.data?.message || error.message))
      } finally {
        isSubmitting.value = false
      }
    }

    const resetForm = () => {
      formData.firstName = ''
      formData.lastName = ''
      formData.email = ''
      formData.phone = ''
      formData.answers = []
      formData.consent = false
      showResults.value = false
      analysisResult.value = null
    }


    const getTagClass = (score) => {
      if (score >= 80) return 'excellent';
      if (score >= 60) return 'good';
      return 'moderate';
    }

    const getEignungText = (score) => {
      if (score >= 80) return 'Hervorragende Eignung';
      if (score >= 60) return 'Gute Eignung';
      return 'Moderate Eignung';
    }

    const bookConsultation = () => {
      window.open('https://www.theaesthetics.at/leistungen/mia-femtech/#termin', '_blank');
    }

    const downloadInfo = () => {
      window.open('https://www.theaesthetics.at/leistungen/mia-femtech/', '_blank');
    }

    return {
      formData,
      questions,
      isSubmitting,
      showResults,
      analysisResult,
      submitForm,
      resetForm,
      getTagClass,
      getEignungText,
      bookConsultation,
      downloadInfo,
      emailError,
      showPrivacyModal,
    }
  }
}
</script>

<style lang="scss">
@import '../assets/BreastHarmonyForm.scss';
</style>